import {
  useAllBackofficeConstants,
  useAllConstants,
} from "components/constants/apiConstants";
import CURRENCIES from "components/constants/currencies";
import useEnvironmentVariables from "components/hooks/useEnvironmentVariables";
import { useMultipleQuery } from "components/shared/useMultipleQuery";
import { useAllDealFlow } from "models/dealflows/apiDealFlow";
import { useAllTeamSources } from "models/deals/apiDeal";
import { useAllUser } from "models/users/apiUser";
import { createContext, useContext, useMemo } from "react";
import AppLoading from "../ui/atoms/AppLoading";

export const ConstantContext = createContext();
const BackofficeConstantContext = createContext();

const ConstantProvider = ({ children }) => {
  const { data, refetch, isSuccess, isError, isLoading, errors } =
    useMultipleQuery({
      users: useAllUser(),
      dealFlows: useAllDealFlow(),
      teamSources: useAllTeamSources(),
      environmentVariables: useEnvironmentVariables(),
    });

  const {
    data: data2,
    isSuccess: isSuccess2,
    isError: isError2,
    isLoading: isLoading2,
    errors: errors2,
  } = useAllConstants();

  // Local constants
  data.currencies = CURRENCIES;

  const constants = useMemo(() => {
    return {
      ...data,
      ...data2,
      ...refetch,
    };
  }, [data, data2, refetch]);

  if (isLoading || isLoading2) return <AppLoading />;
  if (isError || !isSuccess || isError2 || !isSuccess2)
    return <>Errors: {JSON.stringify(errors | errors2)}</>;

  let data3 = { ...data, ...data2 };
  Object.keys(data).forEach((key) => {
    if (JSON.stringify(data[key]) !== JSON.stringify(data3[key])) {
      console.log("Data mismatch", key, data[key], data2[key]);
    }
  });

  return (
    <ConstantContext.Provider value={constants}>
      {children}
    </ConstantContext.Provider>
  );
};

export const ConstantBackofficeProvider = ({ children }) => {
  const { data, isSuccess, isError, isLoading, errors, refetch } =
    useAllBackofficeConstants();

  const constants = useMemo(() => {
    return {
      ...data,
      ...refetch,
    };
  }, [data, refetch]);

  if (isLoading) return <AppLoading />;
  if (isError || !isSuccess) return <>Errors: {JSON.stringify(errors)}</>;

  return (
    <BackofficeConstantContext.Provider value={constants}>
      {children}
    </BackofficeConstantContext.Provider>
  );
};

export const useBackofficeConstant = (modelName, id) => {
  const constants = useContext(BackofficeConstantContext);
  if (!constants) {
    console.error("Backoffice constants not available");
    return {};
  }
  if (!modelName) {
    return constants;
  }
  if (!id) return constants[modelName] || {};
  return constants[modelName].find((item) => item.id === id) || {};
};

export const useBackofficeConstantDico = (modelName) => {
  const constants = useContext(BackofficeConstantContext);
  if (!constants) {
    console.error("Backoffice constants not available");
    return {};
  }
  const dico = constants[modelName] || {};
  return Object.fromEntries(
    Object.entries(dico).map(([key, value]) => [value.id, value]),
  );
};

export const useBackofficeConstantOptions = (modelName) => {
  const constants = useContext(BackofficeConstantContext);
  if (!constants) {
    console.error("Backoffice constants not available");
    return [];
  }
  return (constants[modelName] || []).map((item) => ({
    label: item._title,
    value: item.id,
    obj: item,
  }));
};

export default ConstantProvider;
